import { AssetFrontendSupportLangflowAiApi } from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import { ChatServerResponse, ChatServerResponseContextFile } from "../components/ChatView";

type DoneEvent = { response: string; contextFile: Array<{ filename: string; fileId: number; pages: string }> };

/**
 * Calls the backend SSE streaming endpoint for Langflow chat.
 * Emits tokens via [onChunk] as they arrive from Langflow.
 * Returns the fully processed response (with link replacements) once the stream ends.
 *
 * Uses fetch + ReadableStream rather than EventSource because the endpoint is a POST.
 * Spring SseEmitter sends named events in the format:
 *   event:chunk\ndata:{...}\n\n
 *   event:done\ndata:{...}\n\n
 */
export async function langflowChatStream(
    orgId: number,
    viewId: number,
    viewValue: string,
    assetId: number,
    query: string,
    onChunk: (text: string) => void,
    getDownloadHref: (fileId: number) => string,
): Promise<ChatServerResponse> {
    const response = await AssetFrontendSupportLangflowAiApi.chatPromptStream(
        orgId,
        viewId,
        viewValue,
        assetId,
        { query },
    );

    if (!response.ok) {
        throw new Error("Langflow stream request failed: " + response.status + " " + response.statusText);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let lastEventName = "";
    let finalResult: ChatServerResponse | null = null;
    let streamError: string | null = null;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        // keep incomplete last line in buffer
        buffer = lines.pop() ?? "";

        for (const line of lines) {
            if (line.startsWith("event:")) {
                lastEventName = line.slice(6).trim();
                continue;
            }
            if (!line.startsWith("data:")) continue;
            const raw = line.slice(5).trim();
            if (!raw) continue;

            try {
                const parsed = JSON.parse(raw);
                if (lastEventName === "chunk") {
                    const chunk = (parsed as { text: string }).text;
                    if (chunk) onChunk(chunk);
                } else if (lastEventName === "done") {
                    const doneData = parsed as DoneEvent;
                    const contextFiles: ChatServerResponseContextFile[] = (doneData.contextFile ?? []).map((f) => ({
                        filename: f.filename,
                        href: getDownloadHref(f.fileId),
                        pages: f.pages,
                    }));
                    finalResult = { response: doneData.response, contextFiles };
                } else if (lastEventName === "error") {
                    streamError = (parsed as { message?: string }).message ?? "Streaming failed";
                }
            } catch {
                // malformed JSON - skip
            }

            // reset after consuming the data line
            lastEventName = "";
        }
    }

    if (streamError) {
        throw new Error("Langflow stream failed: " + streamError);
    }
    if (!finalResult) {
        throw new Error("Langflow stream ended without a done event");
    }
    return finalResult;
}
